import {
  createContext,
  FunctionComponent,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import Api from "src/Config";

import { useDebounce } from "src/hooks/useDebonce";
import { textTruncate } from "src/helpers/truncateText";

export const AccountActivityContext = createContext(null);

type Props = {
  children: ReactElement;
};

const AccountActivityProvider: FunctionComponent<Props> = ({ children }) => {
  const { id } = useParams();
  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  const debouncedValue = useDebounce<string>(searchParam, 500);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["account_activity", debouncedValue, pageNumber],
    staleTime: 30000,
    queryFn: async () => {
      const url = debouncedValue
        ? `tm/transactions/${id}/account-activities?search=${debouncedValue}&page_size=10&page=1`
        : `tm/transactions/${id}/account-activities?page_size=10&page=${
            pageNumber + 1
          }`;
      const { data } = await Api.get(`${url}`);

      console.log(data, "activity");

      const response = data.results.map(
        (res: {
          id: string;
          reference: string;
          amount: string;
          score: number;
          severity: string;

          user: {
            last_name: string;
            first_name: string;
          };
        }) => ({
          id: res.id,
          "transaction ID": res.reference,
          name: `${
            !res?.user?.first_name?.toLowerCase() &&
            !res?.user?.last_name?.toLowerCase()
              ? "N/A"
              : textTruncate(
                  `${res.user.first_name?.toLowerCase() || ""} ${
                    res.user.last_name?.toLowerCase() || ""
                  }`,
                  20,
                )
          }`,
          amount: res.amount,
          "fraud score": res.score,
          severity: res.severity || "N/A",
        }),
      );
      return { response, metadata: data?.metadata };
    },
  });

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.metadata.count / 10));
    }
  }, [data]);

  const handleSearch = (e: any) => {
    setSearchParam(e.target.value);
  };

  const contextValue = useMemo(
    () => ({
      isLoading,
      data,
      error,
      handleSearch,
      searchParam,
      isFetching,
      pageNumber,
      pageCount,
      setPageNumber,
    }),
    [
      isLoading,
      data,
      error,
      handleSearch,
      searchParam,
      isFetching,
      pageNumber,
      pageCount,
      setPageNumber,
    ],
  );
  return (
    <AccountActivityContext.Provider value={contextValue}>
      {children}
    </AccountActivityContext.Provider>
  );
};

export default AccountActivityProvider;
